import { Stack } from "@certn/cascadia/foundations/grid-layout/Stack";
import { css } from "@emotion/css";
import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import * as flags from "./flags";
import { Select } from "../select/Select";
import { SelectItem } from "../select/SelectItem";

type PhoneCountriesSelectProps = {
  value: CountryCode;
  onSelectionChange: (country: CountryCode) => void;
  testID?: string;
  labelID?: string;
  countryAriaLabel?: string;
  isDisabled?: boolean;
};

export function PhoneCountriesSelect({
  testID = "phone-number-country-select",
  onSelectionChange,
  value,
  labelID,
  countryAriaLabel,
  isDisabled,
}: PhoneCountriesSelectProps) {
  const { i18n } = useTranslation();

  const countries = useMemo(() => {
    const regionNames = new Intl.DisplayNames([i18n.language ?? "en"], {
      type: "region",
    });

    return getCountries()
      .map((countryCode) => ({
        flag: flags[countryCode],
        textValue: countryCode,
        regionName: regionNames.of(countryCode),
        countryCode,
        countryCallingCode: getCountryCallingCode(countryCode),
      }))
      .sort((a, b) => {
        if (!a.regionName || !b.regionName) {
          return 0;
        }

        return a.regionName < b.regionName ? -1 : 1;
      });
  }, [i18n.language]);

  const selectedRegion = countries.find(
    (country) => country.countryCode === value
  )?.regionName;
  const accessibleName = countryAriaLabel
    ? [countryAriaLabel, selectedRegion].filter(Boolean).join(", ")
    : undefined;

  return (
    <Select
      testID={testID}
      aria-label={accessibleName}
      aria-labelledby={accessibleName ? undefined : labelID}
      selectedKey={value}
      isDisabled={isDisabled}
      onSelectionChange={(countryCode) =>
        onSelectionChange(countryCode as CountryCode)
      }
      selectValue={({ selectedText }) => {
        const found = countries.find(
          ({ regionName }) => regionName === selectedText
        );
        return found ? (
          <Stack>
            <img
              src={found.flag}
              height="18"
              width="26"
              className={styles.countryIcon}
              alt=""
            />
          </Stack>
        ) : null;
      }}
    >
      {countries.map(
        ({ countryCode, countryCallingCode, regionName, flag }) => (
          <SelectItem
            key={countryCode}
            id={countryCode}
            textValue={regionName}
            className={styles.item}
          >
            <img
              src={flag}
              height="18"
              width="26"
              className={css({
                marginRight: "var(--space-2)",
              })}
              alt=""
            />
            {`${regionName} (+${countryCallingCode})`}
          </SelectItem>
        )
      )}
    </Select>
  );
}

const styles = {
  countryIcon: css({
    marginRight: "var(--space-2)",
  }),
  item: css({
    justifyContent: "flex-start",
  }),
};
